import { useMemo, useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import type { FieldTypeSummary } from "@sga/shared";

/**
 * The left pane. Entirely server-driven from `GET /field-types` — CLAUDE.md lists "hardcoding a
 * field-type list in the UI instead of reading GET /field-types" as a change that gets rejected,
 * because a palette that drifts from the validator produces forms that fail on submit.
 */

const CATEGORY_LABELS: Record<string, string> = {
  layout: "Layout & content",
  basic_info: "Basic info",
  text: "Text",
  number: "Number",
  choice: "Choice",
  matrix: "Matrix",
  date_time: "Date & time",
  rating: "Rating",
  legal: "Legal & consent",
  identifier: "Identifiers",
  system: "System / hidden",
  lookup: "Cascading lookups",
};

const PaletteItem = ({
  fieldType,
  onAdd,
}: {
  fieldType: FieldTypeSummary;
  onAdd: (fieldType: FieldTypeSummary) => void;
}): JSX.Element => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${fieldType.type}`,
    data: { source: "palette", fieldType },
  });

  return (
    <button
      ref={setNodeRef}
      type="button"
      {...listeners}
      {...attributes}
      className={`w-full rounded-md border px-2 py-1.5 text-left text-xs transition hover:border-primary hover:bg-secondary ${
        isDragging ? "opacity-50" : ""
      }`}
      // A real focusable button, not a div with a drag handler: dnd-kit's keyboard sensor needs
      // something focusable, and clicking (or pressing Enter) appends the field without dragging —
      // which is what makes the whole palette usable without a mouse.
      onClick={() => onAdd(fieldType)}
      aria-label={`Add ${fieldType.label} field`}
    >
      {fieldType.label}
    </button>
  );
};

export const FieldPalette = ({
  fieldTypes,
  onAdd,
}: {
  fieldTypes: FieldTypeSummary[];
  onAdd: (fieldType: FieldTypeSummary) => void;
}): JSX.Element => {
  const [query, setQuery] = useState("");

  const grouped = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matching = term
      ? fieldTypes.filter(
          (entry) =>
            entry.label.toLowerCase().includes(term) || entry.type.toLowerCase().includes(term),
        )
      : fieldTypes;

    const byCategory = new Map<string, FieldTypeSummary[]>();
    for (const entry of matching) {
      const list = byCategory.get(entry.category) ?? [];
      list.push(entry);
      byCategory.set(entry.category, list);
    }
    return [...byCategory.entries()];
  }, [fieldTypes, query]);

  return (
    <aside className="flex w-60 shrink-0 flex-col border-r bg-card" aria-label="Field palette">
      <div className="border-b p-3">
        <label htmlFor="palette-search" className="sr-only">
          Search field types
        </label>
        <input
          id="palette-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search fields…"
          className="w-full rounded-md border px-2 py-1.5 text-xs"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {grouped.length === 0 && (
          <p className="text-xs text-muted-foreground">No field types match “{query}”.</p>
        )}
        {grouped.map(([category, entries]) => (
          <section key={category} className="mb-4">
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {CATEGORY_LABELS[category] ?? category}
            </h3>
            <div className="space-y-1">
              {/* Click appends to the end; drag positions precisely. */}
              {entries.map((entry) => (
                <PaletteItem key={entry.type} fieldType={entry} onAdd={onAdd} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </aside>
  );
};
